import { createModalCloseButton } from "./modal-close-button";
import { mountAdminTaskItemCreator } from "./admin-shop-creator";
import {
  createAdminTask,
  deleteAdminTask,
  equalizeAdminTaskProbabilities,
  fetchAdminTaskCrates,
  fetchAdminTaskShopItems,
  fetchAdminTasks,
  resetAdminTasks,
  saveAdminTaskProbabilities,
  updateAdminTask,
  type AdminTaskCrateOption,
  type AdminTaskRow,
  type TaskMetric,
  type TaskRewardKind,
  type TaskSection,
} from "./tasks/api";

const MODE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "normal", label: "Normal" },
  { id: "wasd", label: "WASD" },
  { id: "superdot", label: "Superdot" },
  { id: "roulette", label: "Roulette" },
  { id: "highway", label: "Highway" },
  { id: "darkness", label: "Darkness" },
  { id: "mirror", label: "Mirror" },
  { id: "snake", label: "Snake" },
  { id: "combo", label: "Combo" },
  { id: "colorrush", label: "Color Rush" },
  { id: "heat", label: "Heat" },
  { id: "hunter", label: "Hunter" },
  { id: "pop", label: "POP" },
  { id: "counter", label: "Counter" },
  { id: "aim", label: "AIM" },
  { id: "hook", label: "Hook" },
  { id: "tilt", label: "Tilt" },
  { id: "orbit", label: "Orbit" },
  { id: "delay", label: "Delay" },
  { id: "progression", label: "Progression" },
  { id: "speedrun", label: "SpeedRun" },
  { id: "penalty", label: "Golf" },
  { id: "sandbox", label: "Sandbox" },
];

function needsLevel(modeId: string) {
  return modeId === "speedrun" || modeId === "penalty";
}

function isRace(modeId: string) {
  return modeId === "speedrun" || modeId === "penalty";
}

const TASK_PROB_STEP_BP = 100;
const TASK_PROB_TOTAL_BP = 10_000;

function percentToBasisPoints(percent: number) {
  return Math.max(0, Math.round((Number(percent) || 0) * 100));
}

function basisPointsToPercent(bp: number) {
  return bp / 100;
}

function formatPercent(percent: number) {
  const n = Number(percent) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

function fixBasisPointsTotal(bp: number[]) {
  const total = bp.reduce((sum, value) => sum + value, 0);
  if (!bp.length || total === TASK_PROB_TOTAL_BP) return;
  const diff = TASK_PROB_TOTAL_BP - total;
  const maxIdx = bp.reduce((best, value, index, arr) => (value > arr[best] ? index : best), 0);
  bp[maxIdx] = Math.max(0, bp[maxIdx] + diff);
}

function equalizeLocalPercents(items: Array<{ probabilityPercent: number }>) {
  const n = items.length;
  if (!n) return;
  const base = Math.floor(TASK_PROB_TOTAL_BP / n);
  let remainder = TASK_PROB_TOTAL_BP - base * n;
  items.forEach((item) => {
    const extra = remainder > 0 ? 1 : 0;
    if (remainder > 0) remainder -= 1;
    item.probabilityPercent = basisPointsToPercent(base + extra);
  });
}

function ensureLocalPercents(items: Array<{ probabilityPercent: number }>) {
  if (!items.length) return;
  const bp = items.map((item) => percentToBasisPoints(item.probabilityPercent));
  const total = bp.reduce((sum, value) => sum + value, 0);
  if (total <= 0) {
    equalizeLocalPercents(items);
    return;
  }
  if (total !== TASK_PROB_TOTAL_BP) {
    const normalized = bp.map((value) => Math.round((value * TASK_PROB_TOTAL_BP) / total));
    fixBasisPointsTotal(normalized);
    items.forEach((item, index) => {
      item.probabilityPercent = basisPointsToPercent(normalized[index]);
    });
  }
}

function distributeBasisPoints(bp: number[], indices: number[], amount: number, sign: 1 | -1) {
  if (!indices.length || amount <= 0) return;
  const base = Math.floor(amount / indices.length);
  let extra = amount - base * indices.length;
  for (const index of indices) {
    const delta = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra -= 1;
    if (sign === 1) bp[index] += delta;
    else bp[index] = Math.max(0, bp[index] - delta);
  }
}

function adjustLocalProbability(
  items: Array<{ probabilityPercent: number }>,
  index: number,
  direction: "up" | "down",
) {
  if (items.length < 2 || index < 0 || index >= items.length) return;
  ensureLocalPercents(items);
  const bp = items.map((item) => percentToBasisPoints(item.probabilityPercent));
  const others = bp.map((_, i) => i).filter((i) => i !== index);
  if (direction === "up") {
    const othersTotal = others.reduce((sum, i) => sum + bp[i], 0);
    if (othersTotal < TASK_PROB_STEP_BP) return;
    bp[index] += TASK_PROB_STEP_BP;
    distributeBasisPoints(bp, others, TASK_PROB_STEP_BP, -1);
  } else {
    if (bp[index] < TASK_PROB_STEP_BP) return;
    bp[index] -= TASK_PROB_STEP_BP;
    distributeBasisPoints(bp, others, TASK_PROB_STEP_BP, 1);
  }
  fixBasisPointsTotal(bp);
  items.forEach((item, i) => {
    item.probabilityPercent = basisPointsToPercent(bp[i]);
  });
}

function formatAdminError(raw: unknown): string {
  let msg = "Save failed.";
  if (raw instanceof Error) msg = raw.message;
  else if (typeof raw === "string") msg = raw;
  else if (raw && typeof raw === "object") {
    const o = raw as { message?: unknown; error?: unknown };
    if (typeof o.message === "string") msg = o.message;
    else if (typeof o.error === "string") msg = o.error;
    else {
      try {
        msg = JSON.stringify(raw);
      } catch {
        msg = "Save failed.";
      }
    }
  }
  if (msg === "[object Object]") msg = "Save failed.";
  const map: Record<string, string> = {
    invalid_section: "Invalid section.",
    invalid_mode: "Invalid mode.",
    invalid_metric: "Invalid metric.",
    invalid_goal: "Goal must be ≥ 1.",
    missing_level: "Pick a level for SpeedRun/Golf.",
    invalid_reward_kind: "Invalid reward type.",
    invalid_dots: "Dots amount must be ≥ 1.",
    missing_crate: "Pick a Crate.",
    crate_not_found: "Crate not found (publish it in Shop Crates).",
    missing_cosmetic: "Pick or create a reward item.",
    cosmetic_not_found: "Reward item not found.",
    forbidden: "Missing tasksEditor permission.",
    server_error: "Server error — check DB migrations (rewardDots) and try again.",
    database_not_configured: "Database not configured.",
    create_failed: "Create failed — empty server response.",
    update_failed: "Update failed — empty server response.",
  };
  return map[msg] || msg;
}

function crateLabel(c: AdminTaskCrateOption): string {
  return c.isPublished === false ? `${c.name} (draft)` : c.name;
}

export function mountAdminTasks(root: HTMLElement) {
  root.replaceChildren();

  const toolbar = document.createElement("div");
  toolbar.className = "modeBox adminRecordsFilters adminTasksToolbar";
  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "btn green";
  addBtn.textContent = "Add Task";
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn";
  refreshBtn.textContent = "Refresh";
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "btn";
  resetBtn.textContent = "Reset Tasks";
  toolbar.append(addBtn, refreshBtn, resetBtn);
  root.appendChild(toolbar);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  const list = document.createElement("div");
  list.className = "leaderboardWrap";
  root.appendChild(list);

  const editor = document.createElement("div");
  editor.className = "modeBox adminTasksEditor hidden";
  root.appendChild(editor);

  let tasks: AdminTaskRow[] = [];
  let shopItems: Array<{ id: string; name: string; category: string }> = [];
  let crates: AdminTaskCrateOption[] = [];
  let editingId: string | null = null;
  let draftCosmeticId: string | null = null;
  let selectedCrateId: string | null = null;

  let sectionSelect: HTMLSelectElement;
  let modeSelect: HTMLSelectElement;
  let metricSelect: HTMLSelectElement;
  let goalInput: HTMLInputElement;
  let levelInput: HTMLInputElement;
  let levelRow: HTMLElement;
  let descInput: HTMLInputElement;
  let rewardKindSelect: HTMLSelectElement;
  let shopItemSelect: HTMLSelectElement;
  let dotsInput: HTMLInputElement;
  let activeCheck: HTMLInputElement;
  let exclusiveLabel: HTMLElement;
  let rewardShopRow: HTMLElement;
  let rewardCrateRow: HTMLElement;
  let rewardExclusiveRow: HTMLElement;
  let rewardDotsRow: HTMLElement;
  let crateBtn: HTMLButtonElement;
  let crateMenu: HTMLElement;
  let crateEmptyHint: HTMLElement;
  let editorStatus: HTMLElement;
  let saveBtn: HTMLButtonElement;

  function setStatus(msg: string, where: "top" | "editor" | "both" = "both") {
    if (where === "top" || where === "both") status.textContent = msg;
    if ((where === "editor" || where === "both") && editorStatus) {
      editorStatus.textContent = msg;
    }
  }

  function syncRewardRows() {
    const kind = rewardKindSelect.value as TaskRewardKind;
    rewardShopRow.classList.toggle("hidden", kind !== "shop_item");
    rewardCrateRow.classList.toggle("hidden", kind !== "free_crate");
    rewardExclusiveRow.classList.toggle("hidden", kind !== "exclusive_item");
    rewardDotsRow.classList.toggle("hidden", kind !== "dots");
    if (kind === "free_crate") {
      void ensureCrateOptions();
      crateMenu.classList.add("hidden");
    }
  }

  function syncLevelRow() {
    const race = isRace(modeSelect.value);
    levelRow.classList.toggle("hidden", !needsLevel(modeSelect.value));
    metricSelect.disabled = race;
    if (race) metricSelect.value = "time";
  }

  function syncExclusiveLabel() {
    exclusiveLabel.textContent = draftCosmeticId
      ? `Assigned exclusive id: ${draftCosmeticId}`
      : "No exclusive item assigned yet.";
  }

  function syncCrateButton() {
    const picked = crates.find((c) => c.id === selectedCrateId);
    crateBtn.textContent = picked ? crateLabel(picked) : "Select crate…";
    crateBtn.classList.toggle("adminTasksCrateBtn--empty", !picked);
    crateEmptyHint.classList.toggle("hidden", crates.length > 0);
  }

  function fillCrateMenu() {
    crateMenu.replaceChildren();
    for (const c of crates) {
      const opt = document.createElement("button");
      opt.type = "button";
      opt.className = "adminTasksCrateOption";
      opt.textContent = crateLabel(c);
      if (c.id === selectedCrateId) opt.classList.add("adminTasksCrateOption--active");
      opt.onclick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        selectedCrateId = c.id;
        crateMenu.classList.add("hidden");
        syncCrateButton();
      };
      crateMenu.appendChild(opt);
    }
    syncCrateButton();
  }

  async function ensureCrateOptions() {
    try {
      crates = await fetchAdminTaskCrates();
    } catch {
      /* keep cached */
    }
    fillCrateMenu();
  }

  function fillShopSelect() {
    const prev = shopItemSelect.value;
    shopItemSelect.replaceChildren();
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = shopItems.length ? "Select shop item…" : "No shop items available";
    shopItemSelect.appendChild(placeholder);
    for (const it of shopItems) {
      const o = document.createElement("option");
      o.value = it.id;
      o.textContent = `${it.name} (${it.category})`;
      shopItemSelect.appendChild(o);
    }
    if (prev && shopItems.some((it) => it.id === prev)) shopItemSelect.value = prev;
  }

  function openExclusiveCreator() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen campaignAdminItemCreatorModal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox campaignAdminItemCreatorPanel";
    const title = document.createElement("h2");
    title.textContent = "Create exclusive Task item";
    const mount = document.createElement("div");
    mount.className = "campaignAdminItemCreatorMount";
    const close = () => {
      mount.replaceChildren();
      modal.remove();
    };
    box.append(createModalCloseButton(close), title, mount);
    modal.appendChild(box);
    document.body.appendChild(modal);
    mountAdminTaskItemCreator(mount, {
      permissions: { publish: true, overwrite: false },
      unlockTaskId: editingId,
      onTaskItemPublished: (created) => {
        draftCosmeticId = created.id;
        rewardKindSelect.value = "exclusive_item";
        syncRewardRows();
        syncExclusiveLabel();
        setStatus(`Exclusive item “${created.name}” ready — Save the task to attach it.`);
        close();
      },
    });
  }

  function buildEditor() {
    editor.replaceChildren();
    editor.classList.remove("hidden");
    selectedCrateId = null;

    sectionSelect = document.createElement("select");
    sectionSelect.className = "formInput";
    for (const [id, label] of [
      ["daily", "Daily Tasks"],
      ["weekly", "Weekly Tasks"],
      ["permanent", "Permanent Tasks"],
    ] as const) {
      const o = document.createElement("option");
      o.value = id;
      o.textContent = label;
      sectionSelect.appendChild(o);
    }

    modeSelect = document.createElement("select");
    modeSelect.className = "formInput";
    for (const m of MODE_OPTIONS) {
      const o = document.createElement("option");
      o.value = m.id;
      o.textContent = m.label;
      modeSelect.appendChild(o);
    }
    modeSelect.onchange = syncLevelRow;

    metricSelect = document.createElement("select");
    metricSelect.className = "formInput";
    for (const [id, label] of [
      ["score", "Score"],
      ["time", "Time (seconds)"],
    ] as const) {
      const o = document.createElement("option");
      o.value = id;
      o.textContent = label;
      metricSelect.appendChild(o);
    }

    goalInput = document.createElement("input");
    goalInput.className = "formInput";
    goalInput.type = "number";
    goalInput.min = "1";
    goalInput.value = "1000";

    levelInput = document.createElement("input");
    levelInput.className = "formInput";
    levelInput.type = "number";
    levelInput.min = "1";
    levelInput.value = "1";
    levelRow = document.createElement("div");
    levelRow.className = "formRow hidden";
    levelRow.append(
      Object.assign(document.createElement("span"), { className: "formLabel", textContent: "Level" }),
      levelInput,
    );

    descInput = document.createElement("input");
    descInput.className = "formInput";
    descInput.placeholder = "Optional custom description";

    rewardKindSelect = document.createElement("select");
    rewardKindSelect.className = "formInput";
    for (const [id, label] of [
      ["dots", "Dots"],
      ["exclusive_item", "Exclusive item (create new)"],
      ["shop_item", "Existing Shop item"],
      ["free_crate", "Free Crate roll"],
    ] as const) {
      const o = document.createElement("option");
      o.value = id;
      o.textContent = label;
      rewardKindSelect.appendChild(o);
    }
    rewardKindSelect.onchange = syncRewardRows;

    dotsInput = document.createElement("input");
    dotsInput.className = "formInput";
    dotsInput.type = "number";
    dotsInput.min = "1";
    dotsInput.value = "500";
    rewardDotsRow = document.createElement("div");
    rewardDotsRow.className = "formRow";
    rewardDotsRow.append(
      Object.assign(document.createElement("span"), {
        className: "formLabel",
        textContent: "Dots amount",
      }),
      dotsInput,
    );

    shopItemSelect = document.createElement("select");
    shopItemSelect.className = "formInput";
    fillShopSelect();
    rewardShopRow = document.createElement("div");
    rewardShopRow.className = "formRow hidden";
    rewardShopRow.append(
      Object.assign(document.createElement("span"), {
        className: "formLabel",
        textContent: "Shop item",
      }),
      shopItemSelect,
    );

    crateBtn = document.createElement("button");
    crateBtn.type = "button";
    crateBtn.className = "formInput adminTasksCrateBtn adminTasksCrateBtn--empty";
    crateBtn.textContent = "Select crate…";
    crateMenu = document.createElement("div");
    crateMenu.className = "adminTasksCrateMenu hidden";
    crateMenu.setAttribute("role", "listbox");
    crateEmptyHint = document.createElement("p");
    crateEmptyHint.className = "accessHint adminTasksCrateEmpty hidden";
    crateEmptyHint.textContent =
      "No crates found. Create/publish one in Admin → Shop Crates, then reopen this picker.";
    crateBtn.onclick = async (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      await ensureCrateOptions();
      if (!crates.length) {
        crateMenu.classList.add("hidden");
        crateEmptyHint.classList.remove("hidden");
        return;
      }
      crateMenu.classList.toggle("hidden");
      if (!crateMenu.classList.contains("hidden")) {
        crateMenu.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };
    fillCrateMenu();
    rewardCrateRow = document.createElement("div");
    rewardCrateRow.className = "formRow adminTasksCratePicker hidden";
    rewardCrateRow.append(
      Object.assign(document.createElement("span"), {
        className: "formLabel",
        textContent: "Crate",
      }),
      crateBtn,
      crateMenu,
      crateEmptyHint,
    );

    exclusiveLabel = document.createElement("p");
    exclusiveLabel.className = "accessHint";
    const createExclusiveBtn = document.createElement("button");
    createExclusiveBtn.type = "button";
    createExclusiveBtn.className = "btn green";
    createExclusiveBtn.textContent = "Create exclusive item";
    createExclusiveBtn.onclick = () => openExclusiveCreator();
    const clearExclusiveBtn = document.createElement("button");
    clearExclusiveBtn.type = "button";
    clearExclusiveBtn.className = "btn";
    clearExclusiveBtn.textContent = "Clear";
    clearExclusiveBtn.onclick = () => {
      draftCosmeticId = null;
      syncExclusiveLabel();
    };
    rewardExclusiveRow = document.createElement("div");
    rewardExclusiveRow.className = "formRow hidden";
    const exclusiveActions = document.createElement("div");
    exclusiveActions.className = "rgbRow";
    exclusiveActions.append(createExclusiveBtn, clearExclusiveBtn);
    rewardExclusiveRow.append(
      Object.assign(document.createElement("span"), {
        className: "formLabel",
        textContent: "Exclusive",
      }),
      exclusiveActions,
      exclusiveLabel,
    );

    activeCheck = document.createElement("input");
    activeCheck.type = "checkbox";
    activeCheck.checked = true;
    const activeLabel = document.createElement("label");
    activeLabel.className = "formLabel";
    activeLabel.append(activeCheck, document.createTextNode(" Active (visible to players)"));

    saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "btn green";
    saveBtn.textContent = editingId ? "Save changes" : "Create Task";
    saveBtn.onclick = () => void saveTask();
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    cancelBtn.onclick = () => {
      editor.classList.add("hidden");
      editingId = null;
      draftCosmeticId = null;
      selectedCrateId = null;
      crateMenu.classList.add("hidden");
    };

    editorStatus = document.createElement("p");
    editorStatus.className = "accessHint adminTasksEditorStatus";

    const row = (label: string, el: HTMLElement) => {
      const r = document.createElement("div");
      r.className = "formRow";
      r.append(
        Object.assign(document.createElement("span"), { className: "formLabel", textContent: label }),
        el,
      );
      return r;
    };

    editor.append(
      Object.assign(document.createElement("h3"), {
        className: "adminModerationHeading",
        textContent: editingId ? "Edit Task" : "New Task",
      }),
      row("Section", sectionSelect),
      row("Mode", modeSelect),
      levelRow,
      row("Metric", metricSelect),
      row("Goal", goalInput),
      row("Description", descInput),
      row("Reward type", rewardKindSelect),
      rewardDotsRow,
      rewardExclusiveRow,
      rewardShopRow,
      rewardCrateRow,
      activeLabel,
    );
    const actions = document.createElement("div");
    actions.className = "rgbRow adminTasksEditorActions";
    actions.append(saveBtn, cancelBtn);
    editor.append(actions, editorStatus);
    syncLevelRow();
    syncRewardRows();
    syncExclusiveLabel();
    editor.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  async function saveTask() {
    const section = sectionSelect.value as TaskSection;
    const modeId = modeSelect.value;
    const metric = (isRace(modeId) ? "time" : metricSelect.value) as TaskMetric;
    const goalValue = Math.floor(Number(goalInput.value) || 0);
    if (goalValue < 1) {
      setStatus("Goal must be ≥ 1.");
      editorStatus.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return;
    }
    const rewardKind = rewardKindSelect.value as TaskRewardKind;
    // section: "task" keeps the POST discriminator clear; taskSection holds daily/weekly/permanent.
    const body: Record<string, unknown> = {
      section: "task",
      taskSection: section,
      modeId,
      metric,
      goalValue,
      description: descInput.value.trim() || null,
      rewardKind,
      levelIndex: needsLevel(modeId) ? Math.floor(Number(levelInput.value) || 0) : null,
      active: activeCheck.checked,
    };
    if (rewardKind === "dots") {
      const rewardDots = Math.floor(Number(dotsInput.value) || 0);
      if (rewardDots < 1) {
        setStatus("Dots amount must be ≥ 1.");
        editorStatus.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return;
      }
      body.rewardDots = rewardDots;
    } else if (rewardKind === "exclusive_item") {
      if (!draftCosmeticId) {
        setStatus("Create an exclusive item first.");
        editorStatus.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return;
      }
      body.rewardCosmeticId = draftCosmeticId;
    } else if (rewardKind === "shop_item") {
      if (!shopItemSelect.value) {
        setStatus("Pick a Shop item.");
        editorStatus.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return;
      }
      body.rewardCosmeticId = shopItemSelect.value;
    } else {
      if (!selectedCrateId) {
        setStatus("Pick a Crate.");
        editorStatus.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return;
      }
      body.rewardCrateId = selectedCrateId;
    }

    saveBtn.disabled = true;
    setStatus("Saving…");
    try {
      if (editingId) {
        await updateAdminTask({ id: editingId, ...body });
        setStatus("Task updated.");
      } else {
        await createAdminTask(body);
        setStatus("Task created.");
      }
      editor.classList.add("hidden");
      editingId = null;
      draftCosmeticId = null;
      selectedCrateId = null;
      await reload();
    } catch (e) {
      setStatus(formatAdminError(e));
      editorStatus.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } finally {
      saveBtn.disabled = false;
    }
  }

  function renderList() {
    list.replaceChildren();
    if (!tasks.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "No Tasks yet.";
      list.appendChild(empty);
      return;
    }

    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent =
      "Daily: exactly 5 active tasks are drawn each UTC day. Weekly: up to 10 each UTC week. Permanent: all active until claimed/deactivated. Chance ▲/▼ uses the same weight system as Crates.";
    list.appendChild(hint);

    for (const section of ["daily", "weekly", "permanent"] as const) {
      const sectionTasks = tasks.filter((t) => t.section === section);
      if (!sectionTasks.length) continue;

      const heading = document.createElement("h3");
      heading.className = "adminModerationHeading";
      heading.textContent =
        section === "daily"
          ? "Daily Tasks (pool of 5 / day)"
          : section === "weekly"
            ? "Weekly Tasks (pool of up to 10 / week)"
            : "Permanent Tasks (always available while Active)";
      list.appendChild(heading);

      const weighted = section === "daily" || section === "weekly";
      type LocalProb = { id: string; probabilityPercent: number };
      const localProbs: LocalProb[] = sectionTasks.map((t) => ({
        id: t.id,
        probabilityPercent:
          typeof t.probabilityPercent === "number"
            ? t.probabilityPercent
            : basisPointsToPercent(t.probabilityBasisPoints || 0),
      }));
      if (weighted) ensureLocalPercents(localProbs);

      const toolbar = document.createElement("div");
      toolbar.className = "rgbRow adminTasksProbToolbar";
      if (weighted) {
        const equalizeBtn = document.createElement("button");
        equalizeBtn.type = "button";
        equalizeBtn.className = "btn";
        equalizeBtn.textContent = "Equalize chances";
        equalizeBtn.onclick = async () => {
          try {
            setStatus(`Equalizing ${section}…`, "top");
            const updated = await equalizeAdminTaskProbabilities(section);
            for (const u of updated) {
              const i = tasks.findIndex((t) => t.id === u.id);
              if (i >= 0) tasks[i] = { ...tasks[i], ...u };
            }
            renderList();
            setStatus(`${section} chances equalized.`, "top");
          } catch (e) {
            setStatus(formatAdminError(e), "top");
          }
        };
        const saveBtnLocal = document.createElement("button");
        saveBtnLocal.type = "button";
        saveBtnLocal.className = "btn green";
        saveBtnLocal.textContent = "Save chances";
        saveBtnLocal.onclick = async () => {
          try {
            ensureLocalPercents(localProbs);
            setStatus(`Saving ${section} chances…`, "top");
            const updated = await saveAdminTaskProbabilities(section, localProbs);
            for (const u of updated) {
              const i = tasks.findIndex((t) => t.id === u.id);
              if (i >= 0) tasks[i] = { ...tasks[i], ...u };
            }
            renderList();
            setStatus(`${section} chances saved.`, "top");
          } catch (e) {
            setStatus(formatAdminError(e), "top");
          }
        };
        toolbar.append(equalizeBtn, saveBtnLocal);
        list.appendChild(toolbar);
      }

      const table = document.createElement("table");
      table.className = "leaderboardTable";
      const thead = document.createElement("thead");
      thead.innerHTML = weighted
        ? "<tr><th>Goal</th><th>Reward</th><th>Active</th><th>Chance</th><th></th></tr>"
        : "<tr><th>Goal</th><th>Reward</th><th>Active</th><th></th></tr>";
      table.appendChild(thead);
      const tbody = document.createElement("tbody");

      const rerenderProbCells = () => {
        tbody.querySelectorAll<HTMLElement>("[data-prob-label]").forEach((el) => {
          const id = el.getAttribute("data-prob-label") || "";
          const item = localProbs.find((p) => p.id === id);
          if (item) el.textContent = `${formatPercent(item.probabilityPercent)}%`;
        });
      };

      sectionTasks.forEach((t, index) => {
        const tr = document.createElement("tr");
        const rewardText =
          t.rewardLabel ||
          (t.rewardKind === "dots"
            ? `+${t.rewardDots || 0} Dots`
            : `${t.rewardKind}${t.rewardName ? `: ${t.rewardName}` : ""}`);
        const tdGoal = document.createElement("td");
        tdGoal.textContent = t.displayDescription;
        const tdReward = document.createElement("td");
        tdReward.textContent = rewardText;
        const tdActive = document.createElement("td");
        tdActive.textContent = t.active ? "yes" : "no";
        tr.append(tdGoal, tdReward, tdActive);

        if (weighted) {
          const tdProb = document.createElement("td");
          const wrap = document.createElement("div");
          wrap.className = "adminTasksProbCell";
          const label = document.createElement("span");
          label.setAttribute("data-prob-label", t.id);
          label.textContent = `${formatPercent(localProbs[index].probabilityPercent)}%`;
          const btns = document.createElement("div");
          btns.className = "adminCrateProbBtns";
          const upBtn = document.createElement("button");
          upBtn.type = "button";
          upBtn.className = "btn adminCrateProbBtn";
          upBtn.textContent = "▲";
          upBtn.disabled = localProbs.length < 2;
          upBtn.onclick = () => {
            adjustLocalProbability(localProbs, index, "up");
            rerenderProbCells();
          };
          const downBtn = document.createElement("button");
          downBtn.type = "button";
          downBtn.className = "btn adminCrateProbBtn";
          downBtn.textContent = "▼";
          downBtn.disabled = localProbs.length < 2;
          downBtn.onclick = () => {
            adjustLocalProbability(localProbs, index, "down");
            rerenderProbCells();
          };
          btns.append(upBtn, downBtn);
          wrap.append(label, btns);
          tdProb.appendChild(wrap);
          tr.appendChild(tdProb);
        }

        const actions = document.createElement("td");
        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = "btn";
        editBtn.textContent = "Edit";
        editBtn.onclick = async () => {
          editingId = t.id;
          draftCosmeticId = t.rewardCosmeticId;
          selectedCrateId = t.rewardCrateId;
          try {
            const [items, cr] = await Promise.all([
              fetchAdminTaskShopItems(),
              fetchAdminTaskCrates(),
            ]);
            shopItems = items;
            crates = cr;
          } catch {
            /* keep cached */
          }
          buildEditor();
          sectionSelect.value = t.section;
          modeSelect.value = t.modeId;
          metricSelect.value = t.metric;
          goalInput.value = String(t.goalValue);
          levelInput.value = String(t.levelIndex || 1);
          descInput.value = t.description || "";
          rewardKindSelect.value = t.rewardKind;
          dotsInput.value = String(t.rewardDots || 500);
          activeCheck.checked = t.active !== false;
          fillShopSelect();
          if (t.rewardCosmeticId && t.rewardKind === "shop_item") {
            shopItemSelect.value = t.rewardCosmeticId;
          }
          selectedCrateId = t.rewardCrateId;
          fillCrateMenu();
          syncLevelRow();
          syncRewardRows();
          syncExclusiveLabel();
        };
        const delBtn = document.createElement("button");
        delBtn.type = "button";
        delBtn.className = "btn";
        delBtn.textContent = "Delete";
        delBtn.onclick = async () => {
          if (!confirm(`Delete task “${t.displayDescription}”?`)) return;
          try {
            await deleteAdminTask(t.id);
            setStatus("Deleted.", "top");
            await reload();
          } catch (e) {
            setStatus(formatAdminError(e), "top");
          }
        };
        actions.append(editBtn, delBtn);
        tr.appendChild(actions);
        tbody.appendChild(tr);
      });

      table.appendChild(tbody);
      list.appendChild(table);
    }
  }

  async function reload() {
    setStatus("Loading…", "top");
    try {
      const [t, items, cr] = await Promise.all([
        fetchAdminTasks(),
        fetchAdminTaskShopItems(),
        fetchAdminTaskCrates(),
      ]);
      tasks = t;
      shopItems = items;
      crates = cr;
      renderList();
      setStatus(`${tasks.length} task(s).`, "top");
    } catch (e) {
      setStatus(formatAdminError(e), "top");
    }
  }

  function openResetModal() {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen adminTasksResetModal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox adminTasksResetPanel";

    const title = document.createElement("h2");
    title.textContent = "Reset Tasks";
    const intro = document.createElement("p");
    intro.className = "accessHint";
    intro.textContent =
      "Clears player progress for the current period so they can complete those tasks again. Optional re-spin redraws Daily/Weekly pools.";

    const sectionSelect = document.createElement("select");
    sectionSelect.className = "formInput";
    for (const [id, label] of [
      ["daily", "Daily Tasks"],
      ["weekly", "Weekly Tasks"],
      ["permanent", "Permanent Tasks"],
    ] as const) {
      const o = document.createElement("option");
      o.value = id;
      o.textContent = label;
      sectionSelect.appendChild(o);
    }

    const excludeWrap = document.createElement("div");
    excludeWrap.className = "adminTasksResetExcludeList";

    const reshuffleLabel = document.createElement("label");
    reshuffleLabel.className = "formLabel adminTasksResetReshuffle";
    const reshuffleCheck = document.createElement("input");
    reshuffleCheck.type = "checkbox";
    reshuffleCheck.checked = false;
    reshuffleLabel.append(
      reshuffleCheck,
      document.createTextNode(" Re-spin pool (draw different Daily/Weekly tasks)"),
    );

    const modalStatus = document.createElement("p");
    modalStatus.className = "accessHint";

    const renderExcludeList = () => {
      excludeWrap.replaceChildren();
      const section = sectionSelect.value as TaskSection;
      const sectionTasks = tasks.filter((t) => t.section === section);
      reshuffleLabel.classList.toggle("hidden", section === "permanent");
      if (section === "permanent") reshuffleCheck.checked = false;

      const head = document.createElement("p");
      head.className = "formLabel";
      head.textContent = sectionTasks.length
        ? "Keep progress (do not reset) — check to exclude:"
        : "No tasks in this section.";
      excludeWrap.appendChild(head);

      for (const t of sectionTasks) {
        const row = document.createElement("label");
        row.className = "adminTasksResetExcludeRow";
        const cb = document.createElement("input");
        cb.type = "checkbox";
        cb.value = t.id;
        cb.dataset.taskId = t.id;
        const text = document.createElement("span");
        text.textContent = `${t.displayDescription}${t.active ? "" : " (inactive)"}`;
        row.append(cb, text);
        excludeWrap.appendChild(row);
      }
    };

    sectionSelect.onchange = renderExcludeList;
    renderExcludeList();

    const confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    confirmBtn.className = "btn green";
    confirmBtn.textContent = "Reset section";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";

    const close = () => modal.remove();
    cancelBtn.onclick = close;

    confirmBtn.onclick = async () => {
      const taskSection = sectionSelect.value as TaskSection;
      const excludeTaskIds = Array.from(
        excludeWrap.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked"),
      )
        .map((el) => el.dataset.taskId || el.value)
        .filter(Boolean);
      const reshuffle = reshuffleCheck.checked && taskSection !== "permanent";
      const label =
        taskSection === "daily" ? "Daily" : taskSection === "weekly" ? "Weekly" : "Permanent";
      if (
        !confirm(
          `Reset ${label} Tasks?\n\nProgress cleared for current period${
            reshuffle ? "\nPool will be re-spun" : ""
          }${excludeTaskIds.length ? `\n${excludeTaskIds.length} task(s) excluded` : ""}.`,
        )
      ) {
        return;
      }
      confirmBtn.disabled = true;
      modalStatus.textContent = "Resetting…";
      try {
        const result = await resetAdminTasks({ taskSection, excludeTaskIds, reshuffle });
        modalStatus.textContent = `Done — cleared ${result.progressDeleted} progress row(s), reset ${
          result.resetTaskCount
        } task(s)${result.reshuffled ? `, re-spun ${result.reshuffledTaskIds?.length ?? 0} into pool` : ""}.`;
        setStatus(
          `Reset ${result.section}: ${result.progressDeleted} progress cleared` +
            (result.reshuffled ? ", pool re-spun." : "."),
          "top",
        );
        await reload();
        window.setTimeout(close, 900);
      } catch (e) {
        modalStatus.textContent = formatAdminError(e);
        confirmBtn.disabled = false;
      }
    };

    const actions = document.createElement("div");
    actions.className = "rgbRow adminTasksResetActions";
    actions.append(confirmBtn, cancelBtn);

    const sectionRow = document.createElement("div");
    sectionRow.className = "formRow";
    sectionRow.append(
      Object.assign(document.createElement("span"), {
        className: "formLabel",
        textContent: "Section",
      }),
      sectionSelect,
    );

    box.append(
      createModalCloseButton(close),
      title,
      intro,
      sectionRow,
      excludeWrap,
      reshuffleLabel,
      actions,
      modalStatus,
    );
    modal.appendChild(box);
    document.body.appendChild(modal);
  }

  addBtn.onclick = async () => {
    editingId = null;
    draftCosmeticId = null;
    selectedCrateId = null;
    try {
      const [items, cr] = await Promise.all([fetchAdminTaskShopItems(), fetchAdminTaskCrates()]);
      shopItems = items;
      crates = cr;
    } catch {
      /* keep cached */
    }
    buildEditor();
  };
  refreshBtn.onclick = () => void reload();
  resetBtn.onclick = () => openResetModal();

  document.addEventListener(
    "click",
    (ev) => {
      if (!crateMenu || crateMenu.classList.contains("hidden")) return;
      const t = ev.target as Node | null;
      if (t && (crateMenu.contains(t) || crateBtn.contains(t))) return;
      crateMenu.classList.add("hidden");
    },
    true,
  );

  void reload();

  return {
    refresh: reload,
  };
}
